import HlsJs from 'hls.js'
import { VideoAccessError, VideoStartEvent, VideoStateChangeEvent } from '../../types'
import { BaseVideoPlayer } from './BaseVideoPlayer'
import { getVideoIdentifier, isErtDebugModeEnabled } from '../../rrwebUtils'
import { createRef, RefObject } from 'react'
import { flushSync } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { getRenderCallback } from './PlayerError'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.ErtHlsPlayer)

export class HlsPlayer extends BaseVideoPlayer {
  constructor(
    initialParent: HTMLElement,
    videoStartEvent: VideoStartEvent,
    videoStateChangeEvents: VideoStateChangeEvent[],
    options?: { mockVideoElement?: HTMLVideoElement }
  ) {
    const videoShellRef = createRef<HTMLDivElement>()
    const videoElementRef = createRef<HTMLVideoElement>()
    renderVideo(initialParent, videoShellRef, videoElementRef, videoStartEvent)

    // this allows us to instantiate this player in tests
    videoElementRef.current = options?.mockVideoElement ?? videoElementRef.current

    const hlsPlayer = new HlsJs({
      maxBufferHole: 0,
      nudgeOffset: 0
    })

    hlsPlayer.attachMedia(videoElementRef.current!)

    hlsPlayer.on(HlsJs.Events.MEDIA_ATTACHED, () => {
      hlsPlayer.loadSource(videoStartEvent.streamingSessionURL!)
    })

    hlsPlayer.on(HlsJs.Events.ERROR, (_, data) => {
      const status = data.response?.code
      logger.debug('hls error', status)

      // This will prevent the player from spinning indefinitely if it tries to load a video it doesn't have access to
      if ([401, 403].includes(status ?? 0)) {
        videoStartEvent.videoAccessError = {
          videoOmitted: false,
          status: status ?? 0,
          error: 'Error',
          message: 'Video could not be loaded'
        } satisfies VideoAccessError

        const renderCallback = getRenderCallback(initialParent, videoShellRef, videoStartEvent)
        flushSync(renderCallback)

        this.videoElement = undefined
        if (videoShellRef.current) {
          this.videoShell = videoShellRef.current
        }
      }
    })

    if (!videoShellRef.current || !videoElementRef.current) {
      throw new Error('Video shell or element not properly initialized')
    }

    super(initialParent, videoShellRef.current, videoElementRef.current, videoStartEvent, videoStateChangeEvents)
  }
}

function renderVideo(
  appendTo: HTMLElement,
  videoShellRef: RefObject<HTMLDivElement | null>,
  videoElementRef: RefObject<HTMLVideoElement | null>,
  videoStartEvent: VideoStartEvent
) {
  flushSync(() => {
    ReactDOM.createRoot(appendTo).render(
      <div ref={videoShellRef} title={isErtDebugModeEnabled() ? getVideoIdentifier(videoStartEvent) : undefined}>
        <video
          ref={videoElementRef}
          preload="auto"
          controls={isErtDebugModeEnabled()}
          style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
        />
      </div>
    )
  })
}
